import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Skeleton, Tab, Tabs,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useParams, useNavigate } from "react-router-dom";
import {
  getPortfolioById, updatePortfolio,
  removeHoldingFromPortfolio, deletePortfolio,
} from "../../../api/portfolio_api";
import AddHoldingsDialog from "./addHoldingsDialog";
import EditSharesDialog from "./editSharesCountDialog";
import RebalanceDialog from "./RebalanceDialog";
import BulkEditDialog from "./BulkEditDialog";
import SummaryTab from "./SummaryTab";
import HealthCheckTab from "./HealthCheckTab";
import OverallRatingTab from "./OverallRatingTab";
import { fmtNumber, fmtPercent } from "./portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency";

/* ── Stat Card ── */
const StatCard = ({ label, value, icon: Icon, color, subtitle }) => (
  <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-md)", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", gap: "var(--spacing-sm)" }}>
    <div style={{ position: "absolute", top: 12, right: 12, width: 32, height: 32, borderRadius: "var(--radius-md)", backgroundColor: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon sx={{ fontSize: 20, color }} />
    </div>
    <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
    <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-bold)", color }}>{value}</Typography>
    {subtitle && <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>{subtitle}</Typography>}
  </div>
);

/* ── Page Skeleton ── */
const PageSkeleton = () => (
  <Box sx={{ minHeight: "100vh", backgroundColor: "var(--color-bg-primary)", px: "var(--spacing-xl)", py: "var(--spacing-md)" }}>
    <Box display="flex" alignItems="center" gap={2} mb={3}>
      <Skeleton variant="rectangular" width={36} height={36} sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-md)" }} />
      <Box>
        <Skeleton variant="rectangular" width={200} height={24} sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-sm)", mb: 0.5 }} />
        <Skeleton variant="rectangular" width={120} height={16} sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-sm)" }} />
      </Box>
    </Box>
    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={3}>
      {[1, 2, 3, 4, 5, 6].map((i) => (<Skeleton key={i} variant="rectangular" height={90} sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-lg)" }} />))}
    </Box>
    <Skeleton variant="rectangular" width="100%" height={400} sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-lg)" }} />
  </Box>
);

/* ══════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════ */
const PortfolioDetail = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEditHolding, setOpenEditHolding] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [openBulkEdit, setOpenBulkEdit] = useState(false);
  const [openRebalance, setOpenRebalance] = useState(false);
  const [confirmDeleteHolding, setConfirmDeleteHolding] = useState(false);
  const [holdingToDelete, setHoldingToDelete] = useState(null);
  const [deletingHolding, setDeletingHolding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
const sym = getCurrencySymbol();

  useEffect(() => { fetchPortfolio(); }, [portfolioId]);

  const fetchPortfolio = async () => {
    setLoading(true);
    const res = await getPortfolioById(portfolioId);
    if (res?.authExpired) { navigate("/login"); return; }
    if (res?.success) { setPortfolio(res.data); setNameValue(res.data.name); }
    setLoading(false);
  };

  const handleSaveName = async () => {
    if (!nameValue.trim() || nameValue === portfolio.name) { setEditingName(false); setNameValue(portfolio.name); return; }
    setSavingName(true);
    const res = await updatePortfolio(portfolio.id, { name: nameValue.trim() });
    setSavingName(false);
    if (res?.success) { await fetchPortfolio(); } else { setNameValue(portfolio.name); }
    setEditingName(false);
  };

  const handleDeleteHolding = async () => {
    if (!holdingToDelete) return;
    setDeletingHolding(true);
    await removeHoldingFromPortfolio(portfolio.id, holdingToDelete.ticker);
    setDeletingHolding(false); setConfirmDeleteHolding(false); setHoldingToDelete(null);
    await fetchPortfolio();
    setSnackbar({ message: `${holdingToDelete.ticker} removed` });
  };

  const handleDeletePortfolio = async () => {
    setDeleting(true);
    const res = await deletePortfolio(portfolio.id);
    setDeleting(false); setConfirmDelete(false);
    if (res?.success) { navigate("/allPortfolio"); }
  };

  if (loading) return <PageSkeleton />;

  if (!portfolio) return (
    <Box sx={{ minHeight: "100vh", bgcolor: "var(--color-bg-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)", mb: "var(--spacing-md)" }}>Portfolio not found</Typography>
        <Button onClick={() => navigate("/allPortfolio")} sx={{ color: "var(--color-primary)", textTransform: "none" }}>Go to All Portfolios</Button>
      </div>
    </Box>
  );

  const holdings = portfolio.holdings || [];
  const totalValue = Number(portfolio.total_value || 0);
  const totalCost = Number(portfolio.total_cost || 0);
  const totalGain = Number(portfolio.total_gain_loss || 0);
  const totalGainPct = Number(portfolio.total_gain_loss_pct || 0);
  const dailyGainLoss = portfolio.daily_gain_loss != null ? Number(portfolio.daily_gain_loss) : null;
  const dailyGainLossPct = portfolio.daily_gain_loss_pct != null ? Number(portfolio.daily_gain_loss_pct) : null;

  const dialogPaper = { sx: { bgcolor: "var(--color-surface)", backgroundImage: "none", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border-primary)" } };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "var(--color-bg-primary)", px: "var(--spacing-xl)", py: "var(--spacing-md)" }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" mb="var(--spacing-lg)" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/allPortfolio")} sx={{ bgcolor: "var(--color-surface)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              {editingName ? (
                <Box display="flex" alignItems="center" gap={0.75}>
                  <input autoFocus value={nameValue} onChange={(e) => setNameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") { setEditingName(false); setNameValue(portfolio.name); } }}
                    style={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", border: "1px solid var(--color-border-secondary)", borderRadius: "var(--radius-md)", padding: "4px 10px", width: 260, outline: "revert", fontFamily: "inherit", backgroundColor: "var(--color-bg-inset)", color: "var(--color-text-primary)" }} />
                  <IconButton size="small" onClick={handleSaveName} disabled={savingName} sx={{ color: "var(--color-success)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
                    {savingName ? <CircularProgress size={16} sx={{ color: "var(--color-success)" }} /> : <CheckIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                  <IconButton size="small" onClick={() => { setEditingName(false); setNameValue(portfolio.name); }} sx={{ color: "var(--color-text-muted)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography variant="title" sx={{ letterSpacing: "-0.01em" }}>{portfolio.name}</Typography>
                  <IconButton size="small" onClick={() => setEditingName(true)} sx={{ color: "var(--color-text-muted)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              )}
            </Box>
            <Box display="flex" gap={1.5} alignItems="center" mt={0.5}>
              <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>{holdings.length} holding{holdings.length !== 1 ? "s" : ""}</Typography>
              <span style={{ color: "var(--color-border-primary)", fontSize: "var(--font-size-sm)" }}>•</span>
              <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>Created {new Date(portfolio.created_at).toLocaleDateString()}</Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Button size="small" onClick={() => setConfirmDelete(true)} sx={{ color: "var(--color-error)", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", textTransform: "none", px: 1.5, py: 0.5, borderRadius: "var(--radius-md)", border: "1px solid var(--color-border-primary)", "&:hover": { bgcolor: "var(--color-error-light)", borderColor: "var(--color-error)" } }}>Delete Portfolio</Button>
          {/* <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-md)", padding: "var(--spacing-sm) var(--spacing-lg)", textAlign: "right", display: "flex", flexDirection: "column" }}>
            <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "2px" }}>Total Value</Typography>
            <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: "var(--color-success)" }}>${fmtNumber(totalValue, 0)}</Typography>
          </div> */}
        </Box>
      </Box>

      {/* Stat Cards — 6 cards, 3-col on desktop */}
      <Box display="grid" gridTemplateColumns={{ xs: "1fr 1fr", md: "repeat(3, 1fr)" }} gap={2} mb="var(--spacing-lg)">
        <StatCard label="Total Value" value={`${sym}${fmtNumber(totalValue)}`} icon={AccountBalanceWalletIcon} color="var(--color-success)" />
        <StatCard label="Total Cost" value={`${sym}${fmtNumber(totalCost)}`} icon={ShowChartIcon} color="var(--color-primary)" />
        <StatCard label="Total Gain/Loss" value={`${totalGain >= 0 ? "+" : ""}${sym}${fmtNumber(totalGain)}`} icon={totalGain >= 0 ? TrendingUpIcon : TrendingDownIcon} color={totalGain >= 0 ? "var(--color-success)" : "var(--color-error)"} />
        <StatCard label="Return %" value={fmtPercent(totalGainPct)} icon={PieChartOutlineIcon} color={totalGainPct >= 0 ? "var(--color-success)" : "var(--color-error)"} subtitle={`${holdings.length} assets`} />
        <StatCard label="Daily Gain/Loss" value={dailyGainLoss != null ? `${dailyGainLoss >= 0 ? "+" : ""}${sym}${fmtNumber(dailyGainLoss)}` : "—"} icon={CalendarTodayIcon} color={dailyGainLoss != null ? (dailyGainLoss >= 0 ? "var(--color-success)" : "var(--color-error)") : "var(--color-text-muted)"} />
        <StatCard label="Daily %" value={dailyGainLossPct != null ? fmtPercent(dailyGainLossPct) : "—"} icon={CalendarTodayIcon} color={dailyGainLossPct != null ? (dailyGainLossPct >= 0 ? "var(--color-success)" : "var(--color-error)") : "var(--color-text-muted)"} />
      </Box>

      {/* Tabs */}
      <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={{ minHeight: 48, "& .MuiTabs-indicator": { bgcolor: "var(--color-primary)", height: 3, borderRadius: "3px 3px 0 0" } }}>
          {[
            { label: "Summary", icon: <SummarizeOutlinedIcon sx={{ fontSize: 18 }} /> },
            { label: "Health FA Score", icon: <HealthAndSafetyOutlinedIcon sx={{ fontSize: 18 }} /> },
            { label: "FA Rating", icon: <StarOutlineIcon sx={{ fontSize: 18 }} /> },
          ].map((tab, i) => (
            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" sx={{ minHeight: 48, textTransform: "none", fontSize: "var(--font-size-base)", fontWeight: activeTab === i ? "var(--font-weight-bold)" : "var(--font-weight-medium)", color: "var(--color-text-secondary)", "&.Mui-selected": { color: "var(--color-text-primary)" }, gap: 1 }} />
          ))}
        </Tabs>
      </div>

      {/* Tab Content */}
      <div style={{ backgroundColor: "var(--color-bg-inset)", border: "1px solid var(--color-border-primary)", borderTop: "none", borderRadius: "0 0 var(--radius-lg) var(--radius-lg)", padding: "var(--spacing-xl)" }}>
        {activeTab === 0 && <SummaryTab portfolio={portfolio} onAdd={() => setOpenAdd(true)} onBulkEdit={() => setOpenBulkEdit(true)} onEditHolding={(h) => { setSelectedHolding(h); setOpenEditHolding(true); }} onDeleteHolding={(h) => { setHoldingToDelete(h); setConfirmDeleteHolding(true); }} onRebalance={() => setOpenRebalance(true)} />}
        {activeTab === 1 && <HealthCheckTab portfolio={portfolio} />}
        {activeTab === 2 && <OverallRatingTab portfolio={portfolio} />}
      </div>

      {/* Dialogs */}
      <AddHoldingsDialog open={openAdd} onClose={() => setOpenAdd(false)} portfolioId={portfolio.id} existingHoldings={holdings} onSaved={fetchPortfolio} />
      <EditSharesDialog open={openEditHolding} onClose={() => { setOpenEditHolding(false); setSelectedHolding(null); }} portfolioId={portfolio.id} holding={selectedHolding} onSaved={async (updated) => { if (updated) setPortfolio(updated); else await fetchPortfolio(); }} />
      <BulkEditDialog open={openBulkEdit} onClose={() => setOpenBulkEdit(false)} portfolio={portfolio} onSaved={fetchPortfolio} />
      <RebalanceDialog open={openRebalance} onClose={() => setOpenRebalance(false)} portfolioId={portfolio.id} onRebalance={async () => { await fetchPortfolio(); setSnackbar({ message: `"${portfolio.name}" rebalanced successfully` }); }} />

      {/* Delete Holding Dialog */}
      <Dialog open={confirmDeleteHolding} onClose={() => setConfirmDeleteHolding(false)} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
        <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)" }}>Remove holding</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
            Are you sure you want to remove{" "}
            <Typography component="span" sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>{holdingToDelete?.ticker}</Typography>
            {holdingToDelete?.company_name && (<> <Typography component="span" sx={{ color: "var(--color-text-muted)" }}>({holdingToDelete.company_name})</Typography></>)}
            {" "}from{" "}
            <Typography component="span" sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>{portfolio.name}</Typography>?
          </Typography>
          {holdingToDelete && (
            <div style={{ marginTop: "var(--spacing-md)", padding: "var(--spacing-md)", backgroundColor: "var(--color-bg-inset)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-border-primary)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--spacing-sm)" }}>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>Shares</Typography>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>{fmtNumber(holdingToDelete.shares, 2)}</Typography>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>Current Value</Typography>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-success)" }}>{sym}{fmtNumber(holdingToDelete.current_value, 2)}</Typography>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button onClick={() => setConfirmDeleteHolding(false)} disabled={deletingHolding} sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--color-text-secondary)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>Cancel</Button>
          <Button variant="contained" disabled={deletingHolding} onClick={handleDeleteHolding} startIcon={deletingHolding ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", bgcolor: "var(--color-error)", "&:hover": { bgcolor: "var(--color-error)" }, boxShadow: "none" }}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Portfolio Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
        <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)" }}>Delete portfolio</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
            Are you sure you want to delete{" "}
            <Typography component="span" sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>{portfolio.name}</Typography>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting} sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--color-text-secondary)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>Cancel</Button>
          <Button variant="contained" disabled={deleting} onClick={handleDeletePortfolio} startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", bgcolor: "var(--color-error)", "&:hover": { bgcolor: "var(--color-error)" }, boxShadow: "none" }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} message={snackbar?.message} onClose={() => setSnackbar(null)}
        ContentProps={{ sx: { fontSize: "var(--font-size-sm)", bgcolor: "var(--color-surface)", color: "var(--color-text-primary)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-border-primary)", minWidth: 0 } }} />
    </Box>
  );
};

export default PortfolioDetail;